import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Constants } from '../../common';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';

export type ExportFormat = 'csv' | 'xls' | 'pdf';

@Component({
    selector: 'app-zignflix-pagination',
    imports: [
        CommonModule,
        FormsModule,
        MatRadioModule,
        MatSelectModule
    ],
    templateUrl: './zignflix-pagination.component.html',
    styleUrl: './zignflix-pagination.component.scss'
})
export class ZignflixPaginationComponent {

  @Input() totalItems = 0;
  @Input() currentPageItems = 0;
  @Input() pageSize: number = Constants.pageSize;
  @Input() pageSizeOptions: number[] = Constants.pageSizeOptions;
  @Input() currentPage = 0; // 0-based index
  @Input() showExport = false;
  @Input() itemName = 'items';

  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();
  @Output() exportSelected = new EventEmitter<ExportFormat>();

  exportMenuOpen = false;
  selectedExport: ExportFormat = 'csv';

  get actualTotalItems(): number {
    // Deduce exact total items if the backend count is artificially inflated or incorrect.
    // If the API returned fewer items than the limit for this page, we know there are no more pages.
    if (this.currentPageItems < this.pageSize) {
      if (this.currentPage === 0) {
        return this.currentPageItems;
      } else {
        return (this.currentPage * this.pageSize) + this.currentPageItems;
      }
    }
    return this.totalItems;
  }

  get totalPages(): number {
    return Math.ceil(this.actualTotalItems / this.pageSize);
  }

  get minLimit(): number {
    const max = (this.currentPage * this.pageSize) + this.pageSize;
    return max > this.actualTotalItems ? this.actualTotalItems : max;
  }

  getPages(): (number | string)[] {
    const total = this.totalPages;
    const current = this.currentPage + 1; // 1-based

    if (total <= 5) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [];

    // Always show first page
    pages.push(1);

    if (current > 3) {
      pages.push('...');
    }

    // Calculate start and end for middle pages
    let start = Math.max(2, current - 1);
    let end = Math.min(total - 1, current + 1);

    // Adjust if we are at the edges
    if (current === 1) {
      end = 3;
    } else if (current === total) {
      start = total - 2;
    }

    for (let i = start; i <= end; i++) {
      if (!pages.includes(i)) {
        pages.push(i);
      }
    }

    if (current < total - 2) {
      pages.push('...');
    }

    // Always show last page
    if (!pages.includes(total)) {
      pages.push(total);
    }

    return pages;
  }

  onPageButtonClick(page: number | string) {
    if (typeof page === 'number') {
      this.changePage(page - 1);
    }
  }

  changePage(pageIndex: number) {
    if (pageIndex < 0 || pageIndex >= this.totalPages) return;
    this.pageChange.emit(pageIndex);
  }

  onPageSizeChange(event: number) {
    this.pageSizeChange.emit(+event);
  }

  toggleExportMenu() {
    this.exportMenuOpen = !this.exportMenuOpen;
  }

  onExportFormatChange(format: ExportFormat) {
    this.selectedExport = format;
  }

  confirmExport() {
    this.exportSelected.emit(this.selectedExport);
    this.exportMenuOpen = false;
  }
}
